import React, { useState, useMemo, useCallback } from "react";
import "./ProductDetailModal.css";

interface Ingredient {
  id: string;
  name: string;
  name_en: string;
}

interface OptionIngredient {
  id: string;
  name: string;
  name_en: string;
}

interface ProductOption {
  id: string;
  id_ingredient: string;
  price_mod: number;
  ingredient: OptionIngredient | null;
}

interface OptionGroup {
  id: string;
  name: string;
  name_en: string | null;
  min_selections: number;
  max_selections: number;
  sort_order: number;
  options: ProductOption[];
}

interface Size {
  id: string;
  name: string;
  name_en: string;
  price: number;
}

interface ProductImage {
  id: string;
  url: string;
  type: string;
}

interface Product {
  id: string;
  title: string;
  description: string;
  price: number;
  has_size: boolean;
  has_option: number;
  snoozed: boolean;
  images: ProductImage[];
  sizes: Size[];
  ingredients: Ingredient[];
  options: ProductOption[];
  option_groups: OptionGroup[];
}

interface CartItemResult {
  productId: string;
  title: string;
  price: number;
  sizeId: string | null;
  sizeName: string | null;
  quantity: number;
  deselectedIngredientIds: string[];
  selectedOptionIds: string[];
  ingredientsTxt: string;
  comments: string;
}

interface Props {
  product: Product;
  lang: string;
  t: (es: string, en: string) => string;
  onAdd: (item: CartItemResult) => void;
  onClose: () => void;
}

const BASE_URL = "https://fooapp.mx";

function resolveImageUrl(path: string | null | undefined): string | null {
  if (!path) return null;
  if (path.startsWith("http")) return path;
  return `${BASE_URL}${path}`;
}

function formatPrice(price: number): string {
  return `$${price.toFixed(2)}`;
}

const ProductDetailModal: React.FC<Props> = ({ product, lang, t, onAdd, onClose }) => {
  // Size
  const [selectedSizeIndex, setSelectedSizeIndex] = useState(0);

  // Ingredients (all selected by default)
  const [selectedIngredientIds, setSelectedIngredientIds] = useState<Set<string>>(
    () => new Set(product.ingredients?.map((i) => i.id) || [])
  );

  // Flat options (legacy)
  const [selectedOptionId, setSelectedOptionId] = useState<string | null>(
    () => product.options?.[0]?.id || null
  );

  // Grouped options: groupId -> Set<optionId>
  const [selectedOptionsByGroup, setSelectedOptionsByGroup] = useState<Record<string, Set<string>>>(
    () => {
      const initial: Record<string, Set<string>> = {};
      if (product.option_groups?.length > 0) {
        for (const group of product.option_groups) {
          if (group.max_selections === 1 && group.options.length > 0) {
            initial[group.id] = new Set([group.options[0].id]);
          } else {
            initial[group.id] = new Set();
          }
        }
      }
      return initial;
    }
  );

  const [comments, setComments] = useState("");
  const [quantity, setQuantity] = useState(1);

  const hasOptionGroups = product.option_groups?.length > 0;
  const hasLegacyOptions = !hasOptionGroups && (product.has_option || 0) > 0 && product.options?.length > 0;

  const totalPrice = useMemo(() => {
    let base = product.price;
    if (product.has_size && product.sizes.length > 0) {
      base = product.sizes[selectedSizeIndex]?.price || product.price;
    }

    let optionMod = 0;
    if (hasOptionGroups) {
      for (const group of product.option_groups) {
        const selected = selectedOptionsByGroup[group.id] || new Set();
        for (const opt of group.options) {
          if (selected.has(opt.id)) {
            optionMod += opt.price_mod || 0;
          }
        }
      }
    } else if (hasLegacyOptions && selectedOptionId) {
      const opt = product.options.find((o) => o.id === selectedOptionId);
      optionMod = opt?.price_mod || 0;
    }

    return (base + optionMod) * quantity;
  }, [selectedSizeIndex, selectedOptionsByGroup, selectedOptionId, quantity, product, hasOptionGroups, hasLegacyOptions]);

  const allRequiredSatisfied = useMemo(() => {
    if (!hasOptionGroups) return true;
    return product.option_groups.every((group) => {
      const selected = selectedOptionsByGroup[group.id] || new Set();
      return selected.size >= group.min_selections;
    });
  }, [hasOptionGroups, product.option_groups, selectedOptionsByGroup]);

  const toggleIngredient = useCallback((id: string) => {
    setSelectedIngredientIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const selectGroupOption = useCallback((groupId: string, optionId: string) => {
    setSelectedOptionsByGroup((prev) => ({
      ...prev,
      [groupId]: new Set([optionId]),
    }));
  }, []);

  const toggleGroupOption = useCallback((groupId: string, optionId: string, max: number) => {
    setSelectedOptionsByGroup((prev) => {
      const current = prev[groupId] || new Set();
      const next = new Set(current);
      if (next.has(optionId)) {
        next.delete(optionId);
      } else if (next.size < max) {
        next.add(optionId);
      }
      return { ...prev, [groupId]: next };
    });
  }, []);

  const handleAdd = useCallback(() => {
    const deselectedIngredientIds = (product.ingredients || [])
      .filter((i) => !selectedIngredientIds.has(i.id))
      .map((i) => i.id);

    let allSelectedOptionIds: string[] = [];
    if (hasOptionGroups) {
      for (const group of product.option_groups) {
        const selected = selectedOptionsByGroup[group.id] || new Set();
        allSelectedOptionIds.push(...Array.from(selected));
      }
    } else if (hasLegacyOptions && selectedOptionId) {
      allSelectedOptionIds = [selectedOptionId];
    }

    // Build ingredients text
    const parts: string[] = [];
    const selectedSize = product.has_size && product.sizes.length > 0
      ? product.sizes[selectedSizeIndex]
      : null;
    if (selectedSize) {
      parts.push(lang === "es" ? selectedSize.name : selectedSize.name_en || selectedSize.name);
    }

    // Option names
    if (hasOptionGroups) {
      for (const group of product.option_groups) {
        const selected = selectedOptionsByGroup[group.id] || new Set();
        for (const opt of group.options) {
          if (selected.has(opt.id)) {
            const name = opt.ingredient
              ? (lang === "es" ? opt.ingredient.name : opt.ingredient.name_en || opt.ingredient.name)
              : "";
            if (name) parts.push(name);
          }
        }
      }
    } else if (hasLegacyOptions && selectedOptionId) {
      const opt = product.options.find((o) => o.id === selectedOptionId);
      if (opt?.ingredient) {
        const name = lang === "es" ? opt.ingredient.name : opt.ingredient.name_en || opt.ingredient.name;
        parts.push(name);
      }
    }

    // Deselected ingredients
    if (deselectedIngredientIds.length === 0) {
      parts.push(t("con todo", "with everything"));
    } else {
      const names = deselectedIngredientIds.map((id) => {
        const ing = product.ingredients.find((i) => i.id === id);
        return ing ? (lang === "es" ? ing.name : ing.name_en || ing.name) : "";
      }).filter(Boolean);
      parts.push(`${t("Sin", "No")} ${names.join(", ")}`);
    }

    onAdd({
      productId: product.id,
      title: product.title,
      price: totalPrice / quantity,
      sizeId: selectedSize?.id || null,
      sizeName: selectedSize ? (lang === "es" ? selectedSize.name : selectedSize.name_en || selectedSize.name) : null,
      quantity,
      deselectedIngredientIds,
      selectedOptionIds: allSelectedOptionIds,
      ingredientsTxt: parts.join(" | "),
      comments,
    });
  }, [product, selectedSizeIndex, selectedIngredientIds, selectedOptionsByGroup, selectedOptionId, comments, quantity, totalPrice, lang, t, onAdd, hasOptionGroups, hasLegacyOptions]);

  const image = product.images?.find((img) => img.type === "cover") || product.images?.[0];
  const imageUrl = resolveImageUrl(image?.url);

  return (
    <div className="pdm-overlay" onClick={onClose}>
      <div className="pdm-modal" onClick={(e) => e.stopPropagation()}>
        <button className="pdm-close" onClick={onClose}>
          <i className="fas fa-times"></i>
        </button>

        <div className="pdm-scroll">
          {/* Image */}
          {imageUrl && (
            <img src={imageUrl} alt={product.title} className="pdm-image" />
          )}

          {/* Info */}
          <div className="pdm-info">
            <h3 className="pdm-title">{product.title}</h3>
            {product.description && <p className="pdm-desc">{product.description}</p>}
            <span className="pdm-price">
              {product.has_size && product.sizes.length > 0
                ? `${t("Desde", "From")} ${formatPrice(Math.min(...product.sizes.map((s) => s.price)))}`
                : formatPrice(product.price)}
            </span>
          </div>

          {/* Sizes */}
          {product.has_size && product.sizes.length > 0 && (
            <div className="pdm-section">
              <h4 className="pdm-section-title">{t("Tamano", "Size")}</h4>
              {product.sizes.map((size, index) => (
                <label key={size.id} className="pdm-radio-row">
                  <input
                    type="radio"
                    name="size"
                    checked={index === selectedSizeIndex}
                    onChange={() => setSelectedSizeIndex(index)}
                  />
                  <span className="pdm-radio-label">
                    {lang === "es" ? size.name : size.name_en || size.name}
                  </span>
                  <span className="pdm-radio-price">{formatPrice(size.price)}</span>
                </label>
              ))}
            </div>
          )}

          {/* Ingredients */}
          {product.ingredients?.length > 0 && (
            <div className="pdm-section">
              <h4 className="pdm-section-title">{t("Ingredientes", "Ingredients")}</h4>
              {product.ingredients.map((ing) => (
                <label key={ing.id} className="pdm-check-row">
                  <input
                    type="checkbox"
                    checked={selectedIngredientIds.has(ing.id)}
                    onChange={() => toggleIngredient(ing.id)}
                  />
                  <span className="pdm-check-label">
                    {lang === "es" ? ing.name : ing.name_en || ing.name}
                  </span>
                </label>
              ))}
            </div>
          )}

          {/* Option Groups */}
          {hasOptionGroups && (
            <>
              {[...product.option_groups].sort((a, b) => a.sort_order - b.sort_order).map((group) => {
                const isRadio = group.max_selections === 1;
                const isRequired = group.min_selections > 0;
                const selectedIds = selectedOptionsByGroup[group.id] || new Set();
                const groupName = lang === "es" ? group.name : group.name_en || group.name;

                return (
                  <div key={group.id} className="pdm-section">
                    <div className="pdm-section-header">
                      <h4 className="pdm-section-title">{groupName}</h4>
                      {isRequired && (
                        <span className="pdm-required">{t("Requerido", "Required")}</span>
                      )}
                      {!isRequired && !isRadio && (
                        <span className="pdm-hint">
                          {t(`Selecciona hasta ${group.max_selections}`, `Select up to ${group.max_selections}`)}
                        </span>
                      )}
                    </div>
                    {group.options.map((opt) => {
                      const optName = opt.ingredient
                        ? (lang === "es" ? opt.ingredient.name : opt.ingredient.name_en || opt.ingredient.name)
                        : "";
                      const priceMod = opt.price_mod || 0;

                      if (isRadio) {
                        return (
                          <label key={opt.id} className="pdm-radio-row">
                            <input
                              type="radio"
                              name={`group-${group.id}`}
                              checked={selectedIds.has(opt.id)}
                              onChange={() => selectGroupOption(group.id, opt.id)}
                            />
                            <span className="pdm-radio-label">{optName}</span>
                            {priceMod > 0 && (
                              <span className="pdm-radio-price">+{formatPrice(priceMod)}</span>
                            )}
                          </label>
                        );
                      }

                      return (
                        <label key={opt.id} className="pdm-check-row">
                          <input
                            type="checkbox"
                            checked={selectedIds.has(opt.id)}
                            onChange={() => toggleGroupOption(group.id, opt.id, group.max_selections)}
                          />
                          <span className="pdm-check-label">{optName}</span>
                          {priceMod > 0 && (
                            <span className="pdm-check-price">+{formatPrice(priceMod)}</span>
                          )}
                        </label>
                      );
                    })}
                  </div>
                );
              })}
            </>
          )}

          {/* Legacy flat options */}
          {hasLegacyOptions && (
            <div className="pdm-section">
              <h4 className="pdm-section-title">{t("Opciones", "Options")}</h4>
              <p className="pdm-hint">{t("Puedes escoger uno", "You can pick one")}</p>
              {product.options.map((opt) => {
                const optName = opt.ingredient
                  ? (lang === "es" ? opt.ingredient.name : opt.ingredient.name_en || opt.ingredient.name)
                  : "";
                const priceMod = opt.price_mod || 0;

                return (
                  <label key={opt.id} className="pdm-radio-row">
                    <input
                      type="radio"
                      name="legacy-option"
                      checked={selectedOptionId === opt.id}
                      onChange={() => setSelectedOptionId(opt.id)}
                    />
                    <span className="pdm-radio-label">{optName}</span>
                    {priceMod > 0 && (
                      <span className="pdm-radio-price">+{formatPrice(priceMod)}</span>
                    )}
                  </label>
                );
              })}
            </div>
          )}

          {/* Comments */}
          <div className="pdm-section">
            <h4 className="pdm-section-title">{t("Comentarios", "Comments")}</h4>
            <textarea
              className="pdm-comments"
              placeholder={t("Ej: sin cebolla, extra salsa...", "E.g. no onion, extra sauce...")}
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              rows={2}
            />
          </div>

          {/* Quantity */}
          <div className="pdm-quantity">
            <button
              className="pdm-qty-btn"
              onClick={() => setQuantity((q) => Math.max(1, q - 1))}
              disabled={quantity <= 1}
            >
              -
            </button>
            <span className="pdm-qty-value">{quantity}</span>
            <button className="pdm-qty-btn" onClick={() => setQuantity((q) => q + 1)}>
              +
            </button>
          </div>
        </div>

        {/* Add to cart */}
        <button
          className="pdm-add-btn"
          onClick={handleAdd}
          disabled={!allRequiredSatisfied}
        >
          {t("Agregar", "Add")} · {formatPrice(totalPrice)}
        </button>
      </div>
    </div>
  );
};

export default ProductDetailModal;
